/**
 * Amen Atlas — Main Entry Point
 * City-based grouping (Radio Garden style)
 */

import { GospelGlobe } from './globe/GospelGlobe.js';
import { AudioPlayer } from './player/AudioPlayer.js';
import { AdManager } from './ads/AdManager.js';
import { initAdRail } from './ads/AdRail.js';
import { getActiveStations, submitSuggestion } from './services/SupabaseClient.js';
import { favoritesManager } from './services/FavoritesManager.js';
import { groupStationsByCity } from './utils/cityGrouper.js';
import { t, setLanguage, getLanguage, getAvailableLanguages, onLanguageChange } from './i18n/index.js';
import { countryCodeToFlag } from './utils/helpers.js';
import './styles/index.css';
import logoUrl from './amenatlas.png';

initAdRail();

// Inject logo URL into all logo img elements
document.querySelectorAll('.logo-img, .topbar-logo').forEach(img => {
  img.src = logoUrl;
});

// ─── App State ──────────────────────────────────────────────────────────────

let globe;
let player;
let adManager;
let stations = [];
let cityPoints = [];  // Grouped by city
let cityMap = new Map();
let showFavoritesOnly = false;

// ─── DOM References ─────────────────────────────────────────────────────────

const globeContainer = document.getElementById('globe-container');
const crosshair = document.getElementById('crosshair');
const crosshairLabel = document.getElementById('crosshair-label');
const audioEl = document.getElementById('radio-audio');
const playerEl = document.getElementById('player');
const searchInput = document.getElementById('search-input');
const stationList = document.getElementById('station-list');
const stationCount = document.getElementById('station-count');
const panelToggle = document.getElementById('panel-toggle');
const sidePanel = document.getElementById('side-panel');
const suggestBtn = document.getElementById('suggest-btn');
const suggestModal = document.getElementById('suggest-modal');
const suggestClose = document.getElementById('suggest-close');
const suggestForm = document.getElementById('suggest-form');
const pageFooter = document.getElementById('page-footer');

// ─── Initialize ─────────────────────────────────────────────────────────────

async function init() {
  console.log('🌍 Amen Atlas — Starting...');

  // 1. Initialize Ad Manager (must be first to track first session)
  adManager = new AdManager();

  // Corner ad close button
  const cornerAdBanner = document.getElementById('corner-ad-banner');
  const cornerAdClose = document.getElementById('corner-ad-close');
  if (cornerAdClose && cornerAdBanner) {
    cornerAdClose.addEventListener('click', () => {
      cornerAdBanner.classList.add('hidden');
    });
  }

  // 2. Initialize Audio Player
  player = new AudioPlayer({
    audioEl,
    playerEl,
  });

  // 3. Initialize Globe (wrapped in try-catch to not break everything)
  try {
    globe = new GospelGlobe({
      container: globeContainer,
      crosshair,
      crosshairLabel,
      onCityTune: (cityPoint) => {
        player.tuneCity(cityPoint);
        highlightActiveCard(cityPoint.id);
        updateShareLink(cityPoint);
        // Mobile: update now-playing bar with first station
        const firstStation = cityPoint.stations[0];
        window._mobileUpdateNowPlaying?.(firstStation);
        setTimeout(updateMobilePlayIcon, 300);
      },
      onCityLeave: () => {
        // Don't stop audio — just remove visual highlight
      },
    });
    console.log('✅ Globe initialized');
  } catch (err) {
    console.error('[App] Globe failed to initialize:', err);
  }

  // 4. Load stations (demo first, then try Supabase)
  stations = getDemoStations();
  loadStationsIntoApp(stations, 'demo');

  // 5. Try to load real stations from Supabase (async, replaces demo if successful)
  try {
    const realStations = await getActiveStations();
    if (realStations && realStations.length > 0) {
      stations = realStations;
      loadStationsIntoApp(stations, 'Supabase');
    }
  } catch (err) {
    console.warn('[App] Supabase unavailable, using demo stations:', err.message);
  }

  // 6. Setup UI interactions
  setupUI();

  // 7. Setup filter tabs
  setupFilterTabs();

  // 7b. Setup language picker in settings
  setupLanguagePicker();

  // 8. Handle deep links (e.g., #city/São%20Paulo/BR or #station/gospel-fm-brasil)
  const handled = handleDeepLink();

  // 9. If no deep link, detect user location → fly to nearest station on first visit
  if (!handled && globe) {
    globe.flyToUserLocation();
  }

  // 10. Lock portrait orientation on mobile/tablet
  if (window.innerWidth <= 1024 && screen.orientation?.lock) {
    screen.orientation.lock('portrait').catch(() => {});
  }
}

// ─── Deep Link Routing ────────────────────────────────────────────────────────

function handleDeepLink() {
  const hash = decodeURIComponent(location.hash.slice(1));
  if (!hash) return false;

  // Format: #city/CityName/CC  (e.g., #city/São Paulo/BR)
  const cityMatch = hash.match(/^city\/(.+)\/([A-Z]{2})$/i);
  if (cityMatch) {
    const [, cityName, cc] = cityMatch;
    const key = `${cityName}|${cc.toUpperCase()}`;
    const city = cityMap.get(key) || cityPoints.find(cp =>
      cp.city.toLowerCase() === cityName.toLowerCase() && cp.country_code === cc.toUpperCase()
    );
    if (city && globe) {
      globe.flyToCity(city);
      player.tuneCity(city);
      highlightActiveCard(city.id);
      return true;
    }
  }

  // Format: #station/slug  (e.g., #station/gospel-fm-brasil)
  const stationMatch = hash.match(/^station\/(.+)$/);
  if (stationMatch) {
    const slug = stationMatch[1];
    for (const cp of cityPoints) {
      const stIdx = cp.stations.findIndex(s => s.slug === slug);
      if (stIdx !== -1 && globe) {
        globe.flyToCity(cp);
        player.tuneCity(cp, stIdx);
        highlightActiveCard(cp.id);
        return true;
      }
    }
  }

  return false;
}

/**
 * Update URL hash when tuning to a city (for sharing)
 */
function updateShareLink(cityPoint) {
  if (!cityPoint) return;
  const hash = `city/${encodeURIComponent(cityPoint.city)}/${cityPoint.country_code}`;
  history.replaceState(null, '', `#${hash}`);
}

// ─── UI Setup ───────────────────────────────────────────────────────────────

function setupUI() {
  // Panel toggle — Gmail-style: hover peek + click to pin
  let panelPinned = true; // starts expanded and pinned

  function expandPanel() {
    sidePanel.classList.remove('collapsed');
    sidePanel.classList.add('expanded');
    if (pageFooter) pageFooter.classList.remove('collapsed');
    if (playerEl) playerEl.classList.remove('collapsed');
  }

  function collapsePanel() {
    sidePanel.classList.add('collapsed');
    sidePanel.classList.remove('expanded');
    if (pageFooter) pageFooter.classList.add('collapsed');
    if (playerEl) playerEl.classList.add('collapsed');
  }

  // Panel toggle removed — tabs now control expand/collapse


  // Hover peek — open temporarily when hovering collapsed panel
  let peekTimeout;

  sidePanel.addEventListener('mouseenter', () => {
    if (sidePanel.classList.contains('collapsed') && !panelPinned) {
      clearTimeout(peekTimeout);
      sidePanel.classList.add('peeking');
      expandPanel();
    }
  });

  sidePanel.addEventListener('mouseleave', () => {
    if (sidePanel.classList.contains('peeking')) {
      peekTimeout = setTimeout(() => {
        sidePanel.classList.remove('peeking');
        collapsePanel();
      }, 300); // small delay so user can move to content
    }
  });

  // Search
  searchInput.placeholder = t('search.placeholder');
  searchInput.addEventListener('input', () => applyFilters());

  // Suggest modal
  suggestBtn?.addEventListener('click', () => {
    suggestModal.classList.remove('hidden');
  });

  suggestClose.addEventListener('click', () => {
    suggestModal.classList.add('hidden');
  });

  // Close modal on backdrop click
  suggestModal.querySelector('.modal-backdrop').addEventListener('click', () => {
    suggestModal.classList.add('hidden');
  });

  // Random station button (desktop)
  document.getElementById('player-random-btn')?.addEventListener('click', () => {
    if (cityPoints.length > 0 && globe) {
      const randomCity = cityPoints[Math.floor(Math.random() * cityPoints.length)];
      globe.flyToCity(randomCity);
      player.tuneCity(randomCity);
      highlightActiveCard(randomCity.id);
    }
  });

  // Suggest form submit
  suggestForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = suggestForm.querySelector('button[type="submit"]');
    btn.disabled = true;
    btn.textContent = t('loading');

    try {
      await submitSuggestion({
        stationName: document.getElementById('suggest-name').value,
        streamUrl: document.getElementById('suggest-url').value,
        websiteUrl: document.getElementById('suggest-website').value,
        countryCode: null,
        city: document.getElementById('suggest-city').value,
        submittedBy: null,
        notes: document.getElementById('suggest-notes').value,
      });

      alert(t('suggest.success'));
      suggestForm.reset();
      suggestModal.classList.add('hidden');
    } catch (err) {
      console.error('[App] Suggestion failed:', err);
      alert(t('suggest.error'));
    } finally {
      btn.disabled = false;
      btn.textContent = t('suggest.submit');
    }
  });

  // Mobile: swipe up to expand panel
  if (window.innerWidth <= 1024) {
    sidePanel.classList.add('collapsed');
    if (pageFooter) pageFooter.classList.add('collapsed');
    if (playerEl) playerEl.classList.add('collapsed');
  }
}

// ─── Filter Tabs (Início / Estações / Busca / Salvos / Config) ──────────────

let currentTab = null; // null when compact

function setupFilterTabs() {
  const sidePanel = document.getElementById('side-panel');
  const filterHome = document.getElementById('filter-home');
  const filterAll = document.getElementById('filter-all');
  const filterSearch = document.getElementById('filter-search');
  const filterSaved = document.getElementById('filter-saved');
  const filterSettings = document.getElementById('filter-settings');
  const homeContent = document.getElementById('home-content');
  const settingsContent = document.getElementById('settings-content');
  const searchContainer = document.getElementById('search-container');
  const panelTitle = document.getElementById('panel-title');

  const tabMap = {
    home:     { btn: filterHome,     title: 'Início' },
    stations: { btn: filterAll,      title: 'Estações' },
    search:   { btn: filterSearch,   title: 'Busca' },
    saved:    { btn: filterSaved,    title: 'Salvos' },
    settings: { btn: filterSettings, title: 'Configuração' },
  };

  function collapsePanel() {
    currentTab = null;
    sidePanel.classList.add('sidebar-compact');
    Object.values(tabMap).forEach(t => t.btn?.classList.remove('active'));
    if (panelTitle) panelTitle.textContent = 'Amen Atlas';

    // Hide all content
    homeContent.classList.add('hidden');
    stationList.classList.add('hidden');
    searchContainer.classList.add('hidden');
    stationCount.classList.add('hidden');
    settingsContent?.classList.add('hidden');
    showFavoritesOnly = false;
  }

  function expandTab(tab) {
    currentTab = tab;
    sidePanel.classList.remove('sidebar-compact');

    // Reset active class on all tabs
    Object.values(tabMap).forEach(t => t.btn?.classList.remove('active'));
    tabMap[tab]?.btn?.classList.add('active');

    // Update title
    if (panelTitle) panelTitle.textContent = tabMap[tab]?.title || '';

    // Hide all content panels
    homeContent.classList.add('hidden');
    stationList.classList.add('hidden');
    searchContainer.classList.add('hidden');
    stationCount.classList.add('hidden');
    settingsContent?.classList.add('hidden');
    showFavoritesOnly = false;

    // Show relevant content
    if (tab === 'home') {
      homeContent.classList.remove('hidden');
    } else if (tab === 'stations') {
      stationList.classList.remove('hidden');
      searchContainer.classList.remove('hidden');
      stationCount.classList.remove('hidden');
      applyFilters();
    } else if (tab === 'search') {
      stationList.classList.remove('hidden');
      searchContainer.classList.remove('hidden');
      applyFilters();
      setTimeout(() => searchInput?.focus(), 100);
    } else if (tab === 'saved') {
      stationList.classList.remove('hidden');
      searchContainer.classList.remove('hidden');
      showFavoritesOnly = true;
      applyFilters();
    } else if (tab === 'settings') {
      settingsContent?.classList.remove('hidden');
    }
  }

  function toggleTab(tab) {
    if (currentTab === tab) {
      collapsePanel();
    } else {
      expandTab(tab);
    }
  }

  filterHome?.addEventListener('click', () => toggleTab('home'));
  filterAll?.addEventListener('click', () => toggleTab('stations'));
  filterSearch?.addEventListener('click', () => toggleTab('search'));
  filterSaved?.addEventListener('click', () => toggleTab('saved'));
  filterSettings?.addEventListener('click', () => toggleTab('settings'));

  // Re-render saved tab when favorites change
  favoritesManager.onChange(() => {
    if (showFavoritesOnly) applyFilters();
  });
}

function getDisplayList() {
  if (showFavoritesOnly) {
    // Filter cityPoints to only those with at least 1 favorited station
    return cityPoints.filter(cp =>
      cp.stations.some(s => favoritesManager.isFavorite(s.id))
    );
  }
  return cityPoints;
}

// ─── Language Picker & UI Translation ─────────────────────────────────────────

function setupLanguagePicker() {
  const toggle = document.getElementById('lang-toggle');
  const accordion = document.getElementById('lang-accordion');
  const langCurrent = document.getElementById('lang-current');
  if (!toggle || !accordion) return;

  const languages = getAvailableLanguages();
  const curLang = getLanguage();

  // Short label map
  const shortLabels = {
    'pt-PT': 'Português PT',
    'pt-BR': 'Português BR',
    'en': 'English',
    'es': 'Español',
    'fr': 'Français',
    'de': 'Deutsch',
    'it': 'Italiano',
  };

  // Set initial current label
  if (langCurrent) langCurrent.textContent = shortLabels[curLang] || curLang;

  // Build language options
  function renderLangOptions() {
    accordion.innerHTML = '';
    const activeLang = getLanguage();
    languages.forEach(lang => {
      const btn = document.createElement('button');
      btn.className = 'lang-option' + (lang.code === activeLang ? ' active' : '');
      btn.innerHTML = `<span class="lang-check">✓</span>${lang.flag} ${lang.label}`;
      btn.addEventListener('click', () => {
        setLanguage(lang.code);
        if (langCurrent) langCurrent.textContent = shortLabels[lang.code] || lang.label;
        // Close accordion after selection
        accordion.classList.remove('open');
        accordion.classList.add('hidden');
        // Re-render to update checkmark
        renderLangOptions();
      });
      accordion.appendChild(btn);
    });
  }

  renderLangOptions();

  // Toggle accordion on click
  toggle.addEventListener('click', () => {
    if (accordion.classList.contains('open')) {
      accordion.classList.remove('open');
      accordion.classList.add('hidden');
    } else {
      accordion.classList.remove('hidden');
      // Small delay for transition
      requestAnimationFrame(() => accordion.classList.add('open'));
    }
  });

  // Listen for language changes and re-translate
  onLanguageChange(() => {
    updateAllTranslations();
  });

  // Initial translation
  updateAllTranslations();
}

function updateAllTranslations() {
  // Update all elements with data-i18n attribute
  document.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n');
    el.textContent = t(key);
  });

  // Update search placeholder
  const searchInput = document.getElementById('search-input');
  if (searchInput) searchInput.placeholder = t('search.placeholder');

  // Update home content
  const homeWelcome = document.querySelector('.home-welcome-text');
  const homeSub = document.querySelector('.home-welcome-sub');
  if (homeWelcome) homeWelcome.textContent = t('home.welcome');
  if (homeSub) homeSub.innerHTML = t('home.welcome.sub');

  // Update tab labels
  const tabLabels = {
    'filter-home': 'tab.home',
    'filter-all': 'tab.stations',
    'filter-search': 'tab.search',
    'filter-saved': 'tab.saved',
    'filter-settings': 'tab.settings',
  };
  Object.entries(tabLabels).forEach(([id, key]) => {
    const btn = document.getElementById(id);
    if (btn) {
      const label = btn.querySelector('.tab-label');
      if (label) label.textContent = t(key);
      btn.title = t(key);
    }
  });

  // Update panel title if a tab is active
  const panelTitle = document.getElementById('panel-title');
  if (panelTitle && currentTab) {
    const tabTitleMap = {
      home: 'tab.home',
      stations: 'tab.stations',
      search: 'tab.search',
      saved: 'tab.saved',
      settings: 'tab.settings',
    };
    panelTitle.textContent = t(tabTitleMap[currentTab] || 'tab.home');
  }

  // Update favorites empty state if visible
  const emptyFav = document.querySelector('.empty-favorites p');
  if (emptyFav) emptyFav.textContent = t('favorites.empty');
}

/**
 * Groups stations by city and loads into Globe + Side Panel.
 */
function loadStationsIntoApp(stationList, source) {
  const result = groupStationsByCity(stationList);
  cityPoints = result.cityPoints;
  cityMap = result.cityMap;

  if (globe) globe.setCityPoints(cityPoints);
  renderStationList(getDisplayList());
  updateStationCount(stationList, cityPoints);
  console.log(`📻 Loaded ${stationList.length} stations in ${cityPoints.length} cities (${source})`);
}

/**
 * Normalize text: remove accents and convert to lowercase.
 * "São Paulo" → "sao paulo", "Göttingen" → "gottingen"
 */
function normalize(text) {
  return (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function applyFilters() {
  const query = normalize(searchInput.value.trim());
  let list = getDisplayList();
  if (query) {
    list = list.filter(cp =>
      normalize(cp.city).includes(query) ||
      normalize(cp.state).includes(query) ||
      normalize(cp.country_name).includes(query) ||
      cp.stations.some(s => normalize(s.name).includes(query))
    );
  }
  renderStationList(list);
}

// ─── Render Station List ────────────────────────────────────────────────────

function renderStationList(list) {
  stationList.innerHTML = '';

  if (list.length === 0 && showFavoritesOnly) {
    stationList.innerHTML = `<div class="empty-favorites">
      <span class="empty-favorites-icon">♡</span>
      <p>${t('favorites.empty')}</p>
    </div>`;
    return;
  }

  list.forEach((cityPoint, index) => {
    const flag = countryCodeToFlag(cityPoint.country_code);
    const wrapper = document.createElement('div');
    wrapper.className = 'city-card-wrapper';
    wrapper.dataset.cityId = cityPoint.id;
    wrapper.style.animationDelay = `${Math.min(index, 15) * 30}ms`;

    const badge = cityPoint.stationCount > 1
      ? `<span class="station-card-badge">${cityPoint.stationCount}</span>`
      : '';

    const chevron = cityPoint.stationCount > 1
      ? `<span class="city-chevron">▸</span>`
      : '';

    // City header card
    const header = document.createElement('div');
    header.className = 'station-card city-header';
    header.innerHTML = `
      <div class="station-card-flag-left">${flag}</div>
      <div class="station-card-info">
        <div class="station-card-name">${escapeHtml(cityPoint.city)}${badge}</div>
        <div class="station-card-location">${escapeHtml([cityPoint.state, cityPoint.country_name].filter(Boolean).join(', ') || '')}</div>
      </div>
      ${chevron}
    `;

    // Sub-list of stations (hidden by default)
    const subList = document.createElement('div');
    subList.className = 'city-stations-list';

    cityPoint.stations.forEach((station, stIdx) => {
      const stCard = document.createElement('div');
      stCard.className = 'city-station-item';
      stCard.innerHTML = `
        <span class="city-station-dot">♪</span>
        <span class="city-station-name">${escapeHtml(station.name)}</span>
      `;
      stCard.addEventListener('click', (e) => {
        e.stopPropagation();
        globe.flyToCity(cityPoint);
        player.tuneCity(cityPoint, stIdx);
        highlightActiveCard(cityPoint.id);
      });
      subList.appendChild(stCard);
    });

    // Click city header
    header.addEventListener('click', () => {
      if (cityPoint.stationCount > 1) {
        // Toggle expand
        wrapper.classList.toggle('expanded');
      }
      // Always fly + tune
      globe.flyToCity(cityPoint);
      player.tuneCity(cityPoint);
      highlightActiveCard(cityPoint.id);
    });

    wrapper.appendChild(header);
    if (cityPoint.stationCount > 1) {
      wrapper.appendChild(subList);
    }
    stationList.appendChild(wrapper);
  });
}

/**
 * Creates an AdSense sidebar ad block.
 * Falls back to a branded placeholder if AdSense is not loaded.
 */
function _createSidebarAd(slotId) {
  const adWrapper = document.createElement('div');
  adWrapper.className = 'sidebar-ad-block';
  adWrapper.id = slotId;

  // Always show placeholder first (works in dev and when AdSense hasn't loaded)
  _fillPlaceholderAd(adWrapper);

  // Try to load real AdSense ad on top
  try {
    const adsenseReady = typeof window.adsbygoogle !== 'undefined';
    if (adsenseReady) {
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.dataset.adFormat = 'fluid';
      ins.dataset.adLayoutKey = '-6g+co+2e-1p+4s';
      ins.dataset.adClient = 'ca-pub-2949324281320747';
      ins.dataset.adSlot = 'auto';
      adWrapper.prepend(ins);
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    }
  } catch (e) {
    console.warn('[Ad] Sidebar AdSense push failed:', e);
  }

  return adWrapper;
}

/**
 * Placeholder ad for dev / pre-AdSense approval
 */
function _fillPlaceholderAd(container) {
  container.innerHTML = `
    <div class="sidebar-ad-placeholder">
      <span class="sidebar-ad-icon">📻</span>
      <span class="sidebar-ad-label">Amen Atlas</span>
      <span class="sidebar-ad-sub">Espaço publicitário</span>
    </div>
  `;
}

function highlightActiveCard(cityId) {
  document.querySelectorAll('.city-card-wrapper').forEach(wrapper => {
    wrapper.classList.toggle('active', wrapper.dataset.cityId === cityId);
  });
}

function updateStationCount(stationList, cities) {
  const countries = new Set(stationList.map(s => s.country_code)).size;
  const countEl = stationCount.querySelector('.count-number');
  const countriesEl = stationCount.querySelector('.count-countries');
  if (countEl) countEl.textContent = cities.length;
  if (countriesEl) countriesEl.textContent = countries;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ─── MOBILE: Tab Bar + Bottom Sheets ────────────────────────────────────────

const isMobile = () => window.matchMedia('(max-width: 1024px)').matches;

function initMobile() {
  if (!isMobile()) return;

  const tabbar = document.getElementById('mobile-tabbar');
  const backdrop = document.getElementById('mobile-sheet-backdrop');
  const mobilePlayBtn = document.getElementById('mobile-play-btn');
  const mobileSuggestBtn = document.getElementById('mobile-suggest-btn');
  let activeSheet = null;

  // Tab clicks
  tabbar?.addEventListener('click', (e) => {
    const btn = e.target.closest('.tab-btn');
    if (!btn) return;
    const tab = btn.dataset.tab;

    if (tab === 'play') {
      if (player) player.toggle();
      return;
    }

    if (tab === 'random') {
      if (cityPoints.length > 0) {
        const randomCity = cityPoints[Math.floor(Math.random() * cityPoints.length)];
        globe.flyToCity(randomCity);
        player.tuneCity(randomCity);
        highlightActiveCard(randomCity.id);
        closeSheet();
      }
      return;
    }

    if (tab === 'suggest') {
      suggestModal?.classList.remove('hidden');
      return;
    }

    // Toggle sheet
    if (activeSheet === tab) {
      closeSheet();
    } else {
      openSheet(tab);
    }
  });

  // Backdrop click → close
  backdrop?.addEventListener('click', closeSheet);

  // Mobile suggest button
  mobileSuggestBtn?.addEventListener('click', () => {
    closeSheet();
    suggestModal?.classList.remove('hidden');
  });

  function openSheet(tab) {
    closeSheet(true); // close existing without animation

    activeSheet = tab;
    const sheet = document.getElementById(`sheet-${tab}`);
    if (!sheet) return;

    // Populate sheet content
    if (tab === 'favorites') populateFavorites();
    if (tab === 'stations') populateStations();

    // Show
    backdrop?.classList.remove('hidden');
    backdrop?.classList.add('visible');
    sheet.classList.remove('hidden');
    // Force reflow for animation
    sheet.offsetHeight;
    sheet.classList.add('visible');

    // Highlight active tab
    tabbar?.querySelectorAll('.tab-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.tab === tab);
    });
  }

  function closeSheet(instant) {
    if (!activeSheet) return;
    const sheet = document.getElementById(`sheet-${activeSheet}`);

    if (instant) {
      sheet?.classList.remove('visible');
      sheet?.classList.add('hidden');
      backdrop?.classList.remove('visible');
      backdrop?.classList.add('hidden');
    } else {
      sheet?.classList.remove('visible');
      backdrop?.classList.remove('visible');
      setTimeout(() => {
        sheet?.classList.add('hidden');
        backdrop?.classList.add('hidden');
      }, 350);
    }

    activeSheet = null;
    tabbar?.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
  }

  // ─── Sheet: Favorites ──────────────────────────────────────
  function populateFavorites() {
    const container = document.getElementById('sheet-favorites-list');
    if (!container) return;
    const favs = favoritesManager.filterFavorites(stations);
    if (!favs.length) {
      container.innerHTML = '<div class="sheet-empty"><div class="sheet-empty-icon">♡</div>Nenhuma estação salva.<br>Toca ♡ para guardar as tuas favoritas.</div>';
      return;
    }
    renderSheetCards(container, favs);
  }

  // ─── Sheet: All Stations (with integrated search) ───────────
  function populateStations() {
    const container = document.getElementById('sheet-stations-list');
    if (!container) return;
    const searchInput = document.getElementById('mobile-search-input');
    const q = normalize((searchInput?.value || '').trim());
    const filtered = q ? stations.filter(s =>
      normalize(s.name).includes(q) ||
      normalize(s.country_name).includes(q) ||
      normalize(s.state).includes(q) ||
      normalize(s.city).includes(q)
    ) : stations;
    renderSheetCards(container, filtered);
  }

  // Search input handler — filters stations list
  const mobileSearchInput = document.getElementById('mobile-search-input');
  mobileSearchInput?.addEventListener('input', () => {
    populateStations();
  });

  // ─── Render cards for any sheet ────────────────────────────
  function renderSheetCards(container, list) {
    container.innerHTML = list.map(s => `
      <div class="sheet-station-card ${player?.currentStation?.id === s.id ? 'active' : ''}" data-id="${s.id}">
        <span class="sheet-station-flag">${countryCodeToFlag(s.country_code)}</span>
        <div class="sheet-station-info">
          <div class="sheet-station-name">${escapeHtml(s.name)}</div>
          <div class="sheet-station-location">${escapeHtml([s.city, s.state].filter(Boolean).join(', ') || s.country_name || '')}</div>
        </div>
      </div>
    `).join('');

    // Click handler
    container.querySelectorAll('.sheet-station-card').forEach(card => {
      card.addEventListener('click', () => {
        const station = stations.find(s => String(s.id) === card.dataset.id);
        if (station) {
          globe.flyToStation(station);
          closeSheet();
        }
      });
    });
  }

  // ─── Swipe down to close ───────────────────────────────────
  document.querySelectorAll('.sheet-handle').forEach(handle => {
    let startY = 0;
    handle.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
    }, { passive: true });
    handle.addEventListener('touchend', (e) => {
      const endY = e.changedTouches[0].clientY;
      if (endY - startY > 50) closeSheet(); // swipe down > 50px
    }, { passive: true });
  });

  // Expose for updates
  window._mobileUpdateNowPlaying = (station) => {
    const np = document.getElementById('mobile-now-playing');
    if (!np || !station) return;
    document.getElementById('mnp-flag').textContent = countryCodeToFlag(station.country_code);
    document.getElementById('mnp-name').textContent = station.name;
    const loc = [station.city, station.state, station.country_name].filter(Boolean).join(', ');
    document.getElementById('mnp-location').textContent = loc || '';
    np.classList.remove('hidden');
  };

  window._mobileHideNowPlaying = () => {
    document.getElementById('mobile-now-playing')?.classList.add('hidden');
  };

  window._mobileCloseSheet = closeSheet;

  // Listen for play state changes to sync mobile play icon
  window.addEventListener('player-state-change', () => {
    updateMobilePlayIcon();
  });
}

function updateMobilePlayIcon() {
  const icon = document.getElementById('mobile-play-icon');
  const btn = document.getElementById('mobile-play-btn');
  if (!icon || !btn) return;

  if (player?.isPlaying) {
    // Pause icon (two bars)
    icon.innerHTML = '<rect x="5" y="3" width="4" height="18" rx="1"/><rect x="15" y="3" width="4" height="18" rx="1"/>';
    btn.classList.add('playing');
  } else {
    // Play icon (triangle)
    icon.innerHTML = '<polygon points="6,3 20,12 6,21"/>';
    btn.classList.remove('playing');
  }
}

// ─── Demo Stations (development only) ───────────────────────────────────────

function getDemoStations() {
  return [
    // São Paulo — 3 stations (test ⏭)
    { id: '1',  name: 'Gospel FM Brasil',    slug: 'gospel-fm-brasil',    latitude: -23.5505, longitude: -46.6333, country_code: 'BR', country_name: 'Brasil', city: 'São Paulo', state: 'São Paulo', stream_url: 'https://stream.gospelfm.com.br/live',    tags: ['gospel'],    source: 'manual' },
    { id: '11', name: 'Rádio Sara Brasil SP', slug: 'radio-sara-sp',      latitude: -23.5489, longitude: -46.6388, country_code: 'BR', country_name: 'Brasil', city: 'São Paulo', state: 'São Paulo', stream_url: 'https://example.com/sara-sp',            tags: ['gospel'],    source: 'manual' },
    { id: '12', name: 'Radio Feliz FM',        slug: 'radio-feliz-fm',      latitude: -23.5510, longitude: -46.6340, country_code: 'BR', country_name: 'Brasil', city: 'São Paulo', state: 'São Paulo', stream_url: 'http://cloud1.cdnseguro.com:5520/;stream.mp3',  tags: ['gospel'],    source: 'manual' },

    // New York — 2 stations
    { id: '2',  name: 'Worship 24/7',        slug: 'worship-247',         latitude: 40.7128,  longitude: -74.0060, country_code: 'US', country_name: 'United States', city: 'New York', stream_url: 'https://example.com/stream1',      tags: ['worship'],   source: 'manual' },
    { id: '13', name: 'Hope FM NYC',          slug: 'hope-fm-nyc',        latitude: 40.7131,  longitude: -74.0055, country_code: 'US', country_name: 'United States', city: 'New York', stream_url: 'https://example.com/hope-nyc',      tags: ['christian'], source: 'manual' },

    // London — 1 station (⏭ disabled)
    { id: '3',  name: 'Praise FM London',    slug: 'praise-fm-london',    latitude: 51.5074,  longitude: -0.1278,  country_code: 'GB', country_name: 'United Kingdom', city: 'London', stream_url: 'https://example.com/stream2',     tags: ['christian'], source: 'manual' },

    // Lisboa — 2 stations
    { id: '4',  name: 'Rádio Maná',          slug: 'radio-mana',          latitude: 38.7223,  longitude: -9.1393,  country_code: 'PT', country_name: 'Portugal', city: 'Lisboa', stream_url: 'https://example.com/stream3',          tags: ['gospel'],    source: 'manual' },
    { id: '14', name: 'Rádio Maria Portugal', slug: 'radio-maria-pt',     latitude: 38.7200,  longitude: -9.1400,  country_code: 'PT', country_name: 'Portugal', city: 'Lisboa', stream_url: 'https://example.com/radio-maria-pt',   tags: ['christian'], source: 'manual' },

    // 1 station cities
    { id: '5',  name: 'Gospel Nigeria FM',   slug: 'gospel-nigeria',      latitude: 6.5244,   longitude: 3.3792,   country_code: 'NG', country_name: 'Nigeria', city: 'Lagos',    stream_url: 'https://example.com/stream4',        tags: ['gospel'],    source: 'manual' },
    { id: '6',  name: 'Hosanna Radio',       slug: 'hosanna-radio',       latitude: -1.2921,  longitude: 36.8219,  country_code: 'KE', country_name: 'Kenya',   city: 'Nairobi',  stream_url: 'https://example.com/stream5',        tags: ['christian'], source: 'manual' },
    { id: '7',  name: 'Grace FM Korea',      slug: 'grace-fm-korea',      latitude: 37.5665,  longitude: 126.9780, country_code: 'KR', country_name: 'South Korea', city: 'Seoul', stream_url: 'https://example.com/stream6',       tags: ['worship'],   source: 'manual' },
    { id: '8',  name: 'Rádio Boa Nova',      slug: 'radio-boa-nova',      latitude: -15.7975, longitude: -47.8919, country_code: 'BR', country_name: 'Brasil', city: 'Brasília',  stream_url: 'https://example.com/stream7',        tags: ['gospel'],    source: 'manual' },
    { id: '9',  name: 'Hillsong Radio',      slug: 'hillsong-radio',      latitude: -33.8688, longitude: 151.2093, country_code: 'AU', country_name: 'Australia', city: 'Sydney', stream_url: 'https://example.com/stream8',        tags: ['worship'],   source: 'manual' },
    { id: '10', name: 'Faith Radio Ghana',   slug: 'faith-radio-ghana',   latitude: 5.6037,   longitude: -0.1870,  country_code: 'GH', country_name: 'Ghana',  city: 'Accra',    stream_url: 'https://example.com/stream9',        tags: ['gospel'],    source: 'manual' },
  ];
}

// ─── Boot ───────────────────────────────────────────────────────────────────

init();
initMobile();

